import { useCallback, useEffect, useRef } from 'react';
import { NavigateOptions, PathParam, useNavigate } from 'react-router-dom';
import { UiPath } from './index';
import { QueryParams, UiPathParams, withParams } from './withParams';

/**
 * Hook that takes a path and optional base parameters and returns a function
 * that can be used to navigate to that path with the given parameters.
 *
 * @param path
 * @param baseParams
 */

// If base parameters are provided, they will be merged with the optional parameters that are passed to the returned function.
export function useNavigateTo<
  Path extends UiPath = UiPath,
  PathParams extends UiPathParams<Path, string | number> = UiPathParams<
    Path,
    string | number
  >
>(
  path: Path,
  baseParams: PathParams & QueryParams
): NavigateTo<Path, PathParams & QueryParams, PathParams & QueryParams>;

// If no base parameters are provided, then all the parameters are required by the returned function.
export function useNavigateTo<
  Path extends UiPath = UiPath,
  PathParams extends UiPathParams<Path, string | number> = UiPathParams<
    Path,
    string | number
  >
>(path: Path): NavigateTo<Path, undefined, PathParams & QueryParams>;

export function useNavigateTo<
  Path extends UiPath = UiPath,
  PathParams extends UiPathParams<Path, string | number> = UiPathParams<
    Path,
    string | number
  >
>(path: Path, baseParams?: PathParams & QueryParams) {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);

  const baseParamsRef = useRef(baseParams);
  useEffect(() => {
    baseParamsRef.current = baseParams;
  }, [baseParams]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  return useCallback(
    (params?: Partial<PathParams & QueryParams>, options?: NavigateOptions) => {
      navigateRef.current(
        withParams(path, {
          ...baseParamsRef.current,
          ...params,
        } as PathParams & QueryParams),
        options
      );
    },
    [path]
  ) as NavigateTo<Path, typeof baseParams, PathParams & QueryParams>;
}

type NavigateTo<
  Path extends UiPath,
  BaseParams,
  AllParams
> = PathParam<Path> extends never
  ? (params?: Partial<AllParams>, options?: NavigateOptions) => void
  : BaseParams extends undefined | void
  ? (params: AllParams, options?: NavigateOptions) => void
  : (params?: Partial<AllParams>, options?: NavigateOptions) => void;
